import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from '../../../services/shared/utils/utils.service';
import { PageType } from '../../enums/page-type/page-type.module';
import { RoleType } from '../../enums/role-type/role-type.module';
import { Page } from '../../../models/page.model';
import { PageService } from '../../../services/page/page.service';
import { SnackbarService } from '../../../services/shared/snackbar/snackbar.service';
import { TranslationService } from '../../../services/translation/translation.service';
import { PageParentStatus } from '../../../models/pageParentStatus.model';
import { RolePageConfig } from '../../../models/rolePageConfig.model';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-add-page-modal',
  templateUrl: './add-page-modal.component.html',
  styleUrl: './add-page-modal.component.css'
})
export class AddPageModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  form: FormGroup;
  pagesParentAvailable: Page[] = [];
  defaultRoles: RolePageConfig[] = [];

  pageTypes = Object.keys(PageType)
  .filter(key => isNaN(Number(key)))
    .map(key => ({ key, value: PageType[key as keyof typeof PageType] }));

  constructor(private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddPageModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { defaultRoles: RolePageConfig[] },
    private utils: UtilsService,
    private pageService: PageService,
    private snackBar: SnackbarService,
    private translationService: TranslationService)
  {
    this.defaultRoles = data.defaultRoles;

    this.form = this.fb.group({
      title: ['', Validators.required],
      type: [PageType.OFFER, Validators.required],
      pageId: ['', Validators.pattern(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)],
      parentPage: [undefined]
    });
  }

  ngOnInit()
  {
    this.getAvailableParentPages();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getAvailableParentPages(): void {
    const formValues = this.form.value;
    var id = formValues.pageId ? formValues.pageId : "00000000-0000-0000-0000-000000000000";
    this.pageService.getAvailableParentPages(id)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (data:PageParentStatus) => {
        this.pagesParentAvailable = data.pagesParentAvailable;
      },
      error: (error) => {
        this.snackBar.showError(this.translationService.translate('Error fetching pages'));
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValues = this.form.value;

    const getAccessibility = (type: string) => {
      const defaultRole = this.defaultRoles?.find(x => x.type === type);
      return {
        visible: defaultRole?.visible ?? true,
        editable: defaultRole?.editable ?? false,
        guidable: defaultRole?.editable ?? false,
        submittable: defaultRole?.submittable ?? type == RoleType.INTERN.toString() ? false : true
      };
    };

    const roles = [
      RoleType.DELIVERY,
      RoleType.CLIENTSERVICE,
      RoleType.SALES,
      RoleType.CUSTOMER,
      RoleType.INTERN
    ].map(type => ({
      name: type,
      isActive: true,
      accessibility: getAccessibility(RoleType[type]),
      guide: ""
    }));

    var page: Page = {
      id: formValues.pageId ? formValues.pageId : this.utils.generateGUID(),
      version: "",
      title: formValues.title,
      type: Number(formValues.type),
      parentPage: formValues.parentPage,
      blocks: [],
      roles: roles,
      offers: [],
      blockDone: { buttonText: "", title: "" },
      blockSummary: { submitButtonText: "" }
    };

    //create page
    this.pageService.createPage(page)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (data: Page) => {
        this.snackBar.showSuccess(this.translationService.translate('Success saving this page'));

        this.dialogRef.close(data.id);
      },
      error: (error) => {
        if(error.status == 400){
          this.snackBar.showError(this.translationService.translate(error.error));
        }
        else{
          this.snackBar.showError(this.translationService.translate('Error saving this page'));
        }
      }
    });
  }
}
